import React, { useState } from 'react';
import { useAuth } from '../components/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, X } from 'lucide-react';

type SectionId = 'opportunity' | 'platform' | 'pillars' | 'gtm' | 'financials' | 'technology' | 'contact';

const sections: { id: SectionId; label: string; icon: string }[] = [
  { id: 'opportunity', label: 'The Opportunity', icon: '📈' },
  { id: 'platform', label: 'The Platform', icon: '🧠' },
  { id: 'pillars', label: '4 Pillars Deep Dive', icon: '⚡' },
  { id: 'gtm', label: 'Go-to-Market', icon: '🎯' },
  { id: 'financials', label: 'Financials & P&L', icon: '💰' },
  { id: 'technology', label: 'Technology', icon: '🔧' },
  { id: 'contact', label: 'Contact', icon: '✉️' },
];

function Opportunity() {
  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">The Opportunity</h2>
        <p className="text-gray-500">Why this market, why this product, why right now.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {[
          { label: 'US physicians', value: '1M+', sub: 'spending avg 2 hrs/day on EHR tasks' },
          { label: 'Rural hospitals at risk', value: '600+', sub: 'need EHR replacement by 2026' },
          { label: 'EHR market (US)', value: '$15B+', sub: 'growing 7% annually' },
        ].map(({ label, value, sub }) => (
          <div key={label} className="bg-blue-50 rounded-xl p-6 border border-blue-100">
            <div className="text-4xl font-bold text-blue-600 mb-1">{value}</div>
            <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Three converging forces</h3>
        <div className="space-y-4">
          {[
            {
              title: '🤖 AI cost curves have collapsed',
              body: 'The cost of running a large language model capable of clinical reasoning has dropped by 100× in 24 months. In 2022, this required a dedicated ML team and millions in compute. Today it\'s a sub-second API call at fractions of a cent. The technical prerequisite for an agent-native EHR didn\'t exist three years ago — it does now, and the window to build on top of it before incumbents catch up is measured in months, not years.',
            },
            {
              title: '📉 Federal reimbursement cuts are accelerating',
              body: 'CMS reimbursement reductions, combined with rising labor costs and payer pressure, have compressed hospital operating margins to near-zero. Independent practices are consolidating or closing entirely. The market can no longer sustain $200–500K annual EHR licensing fees plus $2–5M implementation costs. A new price point exists in this market — and we occupy it.',
            },
            {
              title: '🩺 A new generation of physicians is demanding better',
              body: 'Physicians trained in the last decade grew up with smartphones, instant information, and AI tools. They are not willing to accept 2 hours of nightly EHR documentation as "just part of the job." Burnout rates are at record highs — 63% of physicians report at least one symptom of burnout. This generation will actively choose employers and practices based on the quality of their tools. They will choose the EHR that respects their time and their career.',
            },
          ].map(({ title, body }) => (
            <div key={title} className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="font-semibold text-gray-900 mb-2">{title}</div>
              <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Why we win where Epic can't follow</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border border-gray-200 text-gray-600 font-medium">Dimension</th>
                <th className="text-left p-3 border border-gray-200 text-gray-600 font-medium">Epic</th>
                <th className="text-left p-3 border border-gray-200 text-blue-700 font-medium">myOpenEHR</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Implementation cost', '$2–5M + 18 months', '$0 overlay · 30-day onboarding'],
                ['Annual license', '$200–500K/year', '$3–150K/year depending on size'],
                ['AI approach', 'Screen-first + AI layer on top', 'Agent-native · screens are fallbacks'],
                ['Charting', 'Post-encounter AI draft review', 'Note signed before you leave the room'],
                ['Data lock-in', 'Proprietary formats (Chronicles)', 'FHIR-native · you own your data'],
                ['Target market', '500+ bed health systems', 'Physician offices → rural hospitals → networks'],
                ['Interoperability', 'Care Everywhere (Epic-to-Epic)', 'TEFCA-native · any system'],
              ].map(([dim, epic, ours]) => (
                <tr key={dim} className="border-b border-gray-100">
                  <td className="p-3 border border-gray-200 text-gray-700 font-medium">{dim}</td>
                  <td className="p-3 border border-gray-200 text-gray-500">{epic}</td>
                  <td className="p-3 border border-gray-200 text-blue-600 font-medium">{ours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Platform() {
  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">The Platform</h2>
        <p className="text-gray-500">Saga — a full agent-native EHR built for Epic-parity and beyond.</p>
      </div>

      <div className="bg-gray-950 text-white rounded-2xl p-8">
        <div className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-4">Core Philosophy</div>
        <blockquote className="text-xl font-medium leading-relaxed text-gray-100 border-l-4 border-blue-500 pl-6">
          "The EHR is not a database with screens on top of it. It is a fleet of agents sitting on a FHIR substrate,
          and the screens are fallbacks when the agent cannot complete the task hands-free.
          Every click a physician makes represents an agent that failed."
        </blockquote>
      </div>

      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Four categories of agents</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            {
              icon: '🧠',
              title: 'Chart Companion (Patient-Bound)',
              desc: 'One persistent AI agent per patient. Remembers the full clinical story across every encounter and year. Notices that this is the third time you\'ve ordered this lab and offers a standing order. Flags that the patient missed a mammogram follow-up from 6 months ago. Something no screen-based EHR can do — because there\'s no stateful actor.',
            },
            {
              icon: '👤',
              title: 'Role Agents (User-Bound)',
              desc: 'Separate agents for attending, nurse, coder, and scheduler. Each has tool access scoped to what that role can do under RBAC. They communicate over an internal message bus — when the nurse\'s agent flags a concerning trend, the attending\'s agent gets notified at the next appropriate moment, not as an interrupt.',
            },
            {
              icon: '⚙️',
              title: 'Workflow Agents (Process-Bound)',
              desc: 'The Discharge Agent. The Prior Auth Agent. The Admission Agent. These run continuously, pull work off queues, and hand off to humans only when they hit a decision they\'re not authorized to make. They don\'t need to be summoned — they\'re always running.',
            },
            {
              icon: '🛡️',
              title: 'Guardian Agents (Safety Layer)',
              desc: 'A Critic agent reviews every action any other agent takes that affects the chart. A Redaction agent scrubs PHI from anything leaving the network. An Audit agent writes every decision to an immutable log. These run adversarially to primary agents. Non-negotiable architecture — the biggest structural difference from Epic\'s AI Factory.',
            },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-2xl mb-3">{icon}</div>
              <div className="font-semibold text-gray-900 mb-2">{title}</div>
              <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Epic-parity module coverage</h3>
        <p className="text-gray-500 text-sm mb-5">Saga covers all 30 Epic module domains — reimagined as agent workflows, not screen workflows. Specialty modules (transplant, genomics, research IRB) are Phase 4 with stub interfaces on Day 1.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            ['Clinical Core', 'Encounter, Orders, Meds, Alerts, Inbox'],
            ['Care Settings', 'Ambulatory, ED, Inpatient, OR, ICU'],
            ['Ancillary', 'Lab, Pharmacy, Radiology, Cardiology'],
            ['Revenue Cycle', 'Scheduling, Billing, Prior Auth, Denials'],
            ['Patient & Population', 'Portal, Telehealth, Population Health'],
            ['Platform', 'FHIR, TEFCA, dbt+Omni Analytics, MCP'],
          ].map(([domain, detail]) => (
            <div key={domain} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-sm font-semibold text-gray-900 mb-1">{domain}</div>
              <div className="text-xs text-gray-400">{detail}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">AI Risk Classification</h3>
        <p className="text-gray-500 text-sm mb-4">Every feature in Saga carries an explicit AI risk class. This is not a compliance checkbox — it's how we design the human-in-the-loop boundary for every workflow.</p>
        <div className="space-y-2">
          {[
            { cls: 'Class I', label: 'Advisory', color: 'green', desc: 'AI suggests, human reviews everything before action' },
            { cls: 'Class II', label: 'Co-pilot', color: 'blue', desc: 'Agent acts with explicit clinician confirmation' },
            { cls: 'Class III', label: 'Autonomous (audited)', color: 'amber', desc: 'Autonomous within policy envelope, full audit trail' },
            { cls: 'Class IV', label: 'Do not automate', color: 'red', desc: 'Regulatory or patient safety bright line — deterministic rules only, no AI' },
          ].map(({ cls, label, color, desc }) => (
            <div key={cls} className={`flex items-start gap-4 p-4 rounded-lg border ${
              color === 'green' ? 'bg-green-50 border-green-200' :
              color === 'blue' ? 'bg-blue-50 border-blue-200' :
              color === 'amber' ? 'bg-amber-50 border-amber-200' :
              'bg-red-50 border-red-200'
            }`}>
              <div className={`text-xs font-bold px-2 py-1 rounded flex-shrink-0 ${
                color === 'green' ? 'bg-green-200 text-green-800' :
                color === 'blue' ? 'bg-blue-200 text-blue-800' :
                color === 'amber' ? 'bg-amber-200 text-amber-800' :
                'bg-red-200 text-red-800'
              }`}>{cls}</div>
              <div>
                <div className="text-sm font-semibold text-gray-900">{label}</div>
                <div className="text-sm text-gray-600">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Pillars() {
  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">4 Pillars — Deep Dive</h2>
        <p className="text-gray-500">Every product decision is evaluated against these four outcomes.</p>
      </div>

      {[
        {
          icon: '⏱',
          title: 'Pillar 1: Save Physician Time',
          problem: 'The average physician spends 2–3 hours per day on EHR tasks — documentation, inbox management, order entry, coding review. That\'s 500+ hours per year per physician that isn\'t spent with patients. 63% of physicians report burnout symptoms. Documentation burden is the #1 cited cause.',
          solution: [
            'Ambient voice charting — note is written as you speak with the patient. No post-encounter documentation.',
            'Conversational order entry — "order a CBC, CMP, and troponin" places all three, with clinical rationale, linked to the right problem.',
            'Decision filter — incoming results classified as Actionable vs. FYI. Only what matters hits the inbox.',
            'In-flight billing — codes queued during the encounter, not sent to a coder days later.',
          ],
          targets: ['75% reduction in documentation time', '60% reduction in alert inbox volume', '80%+ of notes signed before leaving the room', 'Provider NPS > 70 within 90 days'],
        },
        {
          icon: '😊',
          title: 'Pillar 2: Patient Delight',
          problem: 'Patients wait 3–7 days for visit notes. Test results arrive without explanation. Billing is opaque. Care instructions are in English even when the patient speaks Spanish. These aren\'t small inconveniences — they drive poor adherence, unnecessary ER visits, and negative reviews.',
          solution: [
            'Same-day note access — the voice-generated note is in the patient portal before they leave the parking lot.',
            'Results in plain language — every lab result automatically generates a patient-facing explanation at reading level 6.',
            'Multi-language — patient instructions generated in the patient\'s preferred language from the same voice note.',
            'Chart Companion memory — "Sarah, I see you\'ve been tracking your blood pressure at home — how have those readings been?" The AI knows.',
          ],
          targets: ['Same-day note access: 100% of encounters', 'Patient satisfaction (HCAHPS): 75%+ target', 'Portal engagement: 50%+ of patients within 30 days', '30-day readmission: 10% reduction'],
        },
        {
          icon: '💰',
          title: 'Pillar 3: Capture Every Dollar',
          problem: 'The average medical practice leaves 15–25% of billable revenue on the table — through missed codes, claim denials, prior auth delays, and late submissions. This is not a billing department problem. It\'s an EHR problem: codes are suggested after the fact by people who weren\'t in the room.',
          solution: [
            'At-point-of-care coding — ICD-10/CPT codes suggested during the encounter from the voice note, not days later.',
            'Touchless submission — 83% of claims submitted without human review based on structured voice output.',
            'Prior auth automation — the Prior Auth Workflow Agent detects auth requirements and initiates with clinical rationale attached before the patient leaves.',
            'Denial prediction — claim validated against payer rules before submission. Catches likely denials before they happen.',
            'Deterministic guardrails — billing rules are deterministic rule engines, never probabilistic AI. No hallucinated codes.',
          ],
          targets: ['20%+ revenue increase within 60 days', '83% touchless claim submission rate', '< 5% first-pass denial rate', 'Days in AR reduced by 30%'],
        },
        {
          icon: '📉',
          title: 'Pillar 4: Lowest TCO',
          problem: 'Epic costs $2–5M to implement and $200–500K/year to license. That\'s before IT staff, training, and the productivity loss during the 18-month implementation. For a 50-physician independent practice or a 100-bed rural hospital, this is existential. The EHR market has priced out the practices that need help most.',
          solution: [
            'Overlay first, replace later — start as a SaaS layer on your existing EHR via FHIR API. No rip-and-replace required.',
            '30-day onboarding — not 18 months. No implementation army.',
            'Open standards — FHIR-native, MCP-compatible. You own your data and can leave at any time.',
            'Predictable SaaS pricing — per-provider per-month. No surprise implementation fees.',
            'Network economics — shared infrastructure across our entire customer base means costs drop as we scale.',
          ],
          targets: ['Physician office: $3–8K/year vs $50K+ for Epic', 'Rural hospital: $50–150K/year vs $500K+ for Epic', 'Payback period: < 6 months from revenue lift alone', 'Gross margin: 70%+ (pure SaaS model)'],
        },
      ].map(({ icon, title, problem, solution, targets }) => (
        <div key={title} className="bg-white border border-gray-200 rounded-2xl p-8 space-y-5">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{icon}</span>
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">The Problem</div>
            <p className="text-sm text-gray-600 leading-relaxed">{problem}</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">How We Solve It</div>
            <ul className="space-y-2">
              {solution.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-blue-500 mt-0.5 flex-shrink-0">→</span>{s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Targets</div>
            <div className="flex flex-wrap gap-2">
              {targets.map((t) => (
                <span key={t} className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 rounded-full">{t}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function GTM() {
  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Go-to-Market</h2>
        <p className="text-gray-500">Win one physician. Win their group. Win their hospital. Win the network.</p>
      </div>

      <div className="bg-blue-600 text-white rounded-2xl p-8">
        <div className="text-blue-200 text-xs font-semibold uppercase tracking-widest mb-3">Core Thesis</div>
        <p className="text-xl font-medium leading-relaxed">
          We don't need to beat Epic everywhere. We need to win in the markets Epic has abandoned —
          independent practices, small groups, and rural hospitals — and build the interoperability
          layer that makes our network more valuable with every new member.
        </p>
      </div>

      <div className="space-y-6">
        {[
          {
            step: '01',
            title: 'Physician Offices & Groups',
            timeline: 'Now → Year 1',
            why: 'Shortest sales cycle (weeks, not months). CFO decision, not IT committee. Immediate ROI from billing automation alone. Becomes a reference customer and referral source for step 2.',
            how: [
              'Direct sales to independent practice owners via physician network referrals',
              'SaaS overlay on existing EHR — no rip-and-replace risk objection',
              'ROI proof: 20%+ revenue increase from billing automation pays for product in Month 1',
              'Target: 50-physician group as first lighthouse customer',
            ],
            economics: '$3–8K/year per practice · CAC $5–8K · Payback < 6 months',
          },
          {
            step: '02',
            title: 'Rural & Community Hospitals',
            timeline: 'Year 1 → Year 2',
            why: '55% of rural hospitals need EHR replacement by 2026. RHT federal funding available (5% of annual revenue cap on EHR spend creates budget room). They can\'t afford Epic\'s price point or 18-month implementation timeline.',
            how: [
              'MEDITECH / Cerner end-of-life customers first — highest urgency, active RFP cycles',
              'RHT grant-eligible pricing — structure contract to fit within federal funding programs',
              'Full EHR replacement on 60-day timeline (vs Epic\'s 18 months)',
              'Target: 250+ rural hospitals, 50–250 beds, no existing Epic footprint',
            ],
            economics: '$50–150K/year per hospital · CAC $15–25K · Payback 9 months',
          },
          {
            step: '03',
            title: 'Hospital Networks (Interoperability Layer)',
            timeline: 'Year 2 → Year 3',
            why: 'Once we have 20+ hospitals and 500+ physician practices on FHIR-native infrastructure, we become the most valuable interoperability layer in regional healthcare. Cross-network population health and analytics is something Epic charges millions for and delivers poorly.',
            how: [
              'Aggregate cross-network insights with patient consent — population health at regional scale',
              'Partner with hospital networks as the FHIR-native data layer (not as a competitor to Epic)',
              'Sell the network analytics layer separately — additional revenue without additional implementation cost',
              'Target: 3–5 regional hospital networks in Year 3',
            ],
            economics: '$500K–2M/year per network · High margin · Network effects compound',
          },
        ].map(({ step, title, timeline, why, how, economics }) => (
          <div key={step} className="bg-white border border-gray-200 rounded-2xl p-8">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{step}</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                <div className="text-sm text-blue-600 font-medium">{timeline}</div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Why This First</div>
                <p className="text-sm text-gray-600 leading-relaxed">{why}</p>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">How We Win</div>
                <ul className="space-y-1">
                  {how.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-blue-500 flex-shrink-0 mt-0.5">→</span>{h}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-500 font-medium">{economics}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Technology() {
  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Technology</h2>
        <p className="text-gray-500">FHIR-native substrate · Agent orchestration · dbt + Omni analytics · Deterministic safety rails.</p>
      </div>

      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🏗️ Architecture Principles</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'FHIR-native from Day 1', desc: 'Every data point is a FHIR resource from the moment it\'s created. Not retrofitted. Not mapped. Native. Ensures full interoperability (Cures Act), portability, and enables our cross-network analytics layer.' },
              { title: 'Agent-native, screen-optional', desc: 'Screens are the fallback for when agents can\'t complete a task hands-free. The primary interface is voice and agent-to-agent communication. This is a fundamental architectural choice — not a UI preference.' },
              { title: 'Deterministic guardrails on safety-critical decisions', desc: 'Billing codes, critical lab thresholds, prior auth requirements, and regulatory rules are handled by deterministic rule engines — never probabilistic AI. An LLM can never decide if a claim is billable or a critical value was crossed.' },
              { title: 'Guardian agents run adversarially', desc: 'A Critic agent reviews every action. A Redaction agent scrubs PHI. An Audit agent writes every decision to an immutable log. These are not optional — they are the safety architecture.' },
            ].map(({ title, desc }) => (
              <div key={title} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <div className="font-semibold text-gray-900 mb-2 text-sm">{title}</div>
                <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Analytics Stack: FHIR → dbt → Omni</h3>
          <p className="text-sm text-gray-600 mb-5 leading-relaxed">
            Other analytics frameworks in healthcare have consistently failed because they mix taxonomy and data definitions with reporting tooling without a semantic layer — leaving every team with their own definition of "patient count" or "revenue." We fix this with a three-layer approach.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { layer: '1. FHIR Substrate', icon: '🏛️', desc: 'All clinical and operational data stored as FHIR R4 resources. Standardized schema across every customer. No proprietary formats.' },
              { layer: '2. dbt Semantic Layer', icon: '📐', desc: 'Metrics defined once in dbt — "readmission rate," "claim denial rate," "provider productivity" — and reused across every report. No more metric drift between dashboards.' },
              { layer: '3. Omni BI', icon: '📈', desc: 'Omni surfaces insights on top of the dbt semantic layer. Purpose-built to work with dbt. No one-off SQL queries that bypass the definitions.' },
            ].map(({ layer, icon, desc }) => (
              <div key={layer} className="bg-blue-50 rounded-xl p-5 border border-blue-100 text-center">
                <div className="text-2xl mb-2">{icon}</div>
                <div className="font-semibold text-gray-900 text-sm mb-2">{layer}</div>
                <p className="text-xs text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🤖 AI Model Strategy</h3>
          <div className="space-y-3">
            {[
              { model: 'Claude (Anthropic)', role: 'Complex reasoning', uses: 'Differential diagnosis, risk stratification, complex prior auth decisions, patient communication drafting', pct: '5% of decisions' },
              { model: 'Mistral 8x7B (on-prem)', role: 'High-volume routing', uses: 'Alert classification, routine billing codes, patient education text, results routing', pct: '95% of decisions' },
              { model: 'Deterministic rule engines', role: 'Safety-critical', uses: 'Critical lab thresholds, billing compliance rules, prior auth requirements, regulatory checks', pct: '100% of Class IV decisions' },
            ].map(({ model, role, uses, pct }) => (
              <div key={model} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-sm">{model} <span className="text-gray-400 font-normal">— {role}</span></div>
                  <div className="text-sm text-gray-600 mt-1">{uses}</div>
                </div>
                <div className="text-sm font-bold text-blue-600 flex-shrink-0">{pct}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            <strong>Cost result:</strong> ~$250/hospital/year in AI API costs vs $3,000+ all-Claude approach. Claude handles the 5% that requires deep reasoning. Mistral handles the rest at $0 marginal cost on-prem.
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🔐 HIPAA & Compliance Architecture</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'PHI never leaves the hospital network — Claude receives only de-identified structured queries',
              'Mistral runs on-prem — zero data egress for 95% of AI decisions',
              'AES-256 encryption at rest, TLS 1.3 in transit',
              'Immutable audit log — every AI decision logged with model, confidence, and clinician action',
              'BAA with Anthropic via AWS Bedrock for Claude API calls',
              'RBAC — role-bound agents enforce what each role can see and do',
              '7-year audit log retention for HIPAA compliance',
              'Breach notification plan + disaster recovery with daily off-site backups',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm text-gray-600">
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>{item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Contact() {
  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Contact</h2>
        <p className="text-gray-500">Pilot programs, partnerships, and investor conversations.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="text-2xl mb-4">🏥</div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Pilot Program</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-5">
            We're running a limited pilot with physician offices and small hospital groups.
            60-day proof of value — measurable ROI or we walk away.
            No implementation fee for pilot participants.
          </p>
          <a href="mailto:johnbcoles@gmail.com?subject=myOpenEHR Pilot Interest" className="inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-5 py-3 rounded-lg hover:bg-blue-700 transition-colors">
            Apply for a pilot →
          </a>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="text-2xl mb-4">🤝</div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Partnerships</h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-5">
            We're looking for FHIR integration partners, EHR migration consultants, and
            hospital networks interested in becoming the interoperability anchor
            for their region.
          </p>
          <a href="mailto:johnbcoles@gmail.com?subject=myOpenEHR Partnership" className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-lg hover:bg-gray-800 transition-colors">
            Explore partnership →
          </a>
        </div>
      </div>

      <div className="bg-blue-600 text-white rounded-2xl p-8">
        <h3 className="text-lg font-bold mb-2">Investor conversations</h3>
        <p className="text-blue-100 text-sm leading-relaxed mb-5">
          We're building the company that makes Epic irrelevant for the bottom 80% of the healthcare market.
          If you invest in healthcare infrastructure or AI-native enterprise software, let's talk.
        </p>
        <a href="mailto:johnbcoles@gmail.com?subject=myOpenEHR Investor Inquiry" className="inline-flex items-center gap-2 bg-white text-blue-600 text-sm font-medium px-5 py-3 rounded-lg hover:bg-blue-50 transition-colors">
          Start a conversation →
        </a>
      </div>

      <div className="bg-gray-50 rounded-xl p-6 text-center">
        <div className="text-sm text-gray-500">
          <strong className="text-gray-700">John Coles</strong> · Founder, myOpenEHR<br />
          <a href="mailto:johnbcoles@gmail.com" className="text-blue-600 hover:underline">johnbcoles@gmail.com</a>
        </div>
      </div>
    </div>
  );
}

function Financials() {
  const pnl = [
    {
      year: '2026 (H2)', phase: 'Build + First Pilots',
      arr_start: '$0', arr_end: '$200K',
      revenue: '$50K', gross_profit: '$20K', gp_pct: '40%',
      personnel: '$750K', ai_infra: '$75K', compliance: '$125K', cyber: '$35K',
      legal: '$60K', sales_mktg: '$25K', ga: '$55K', total_opex: '$1.13M',
      ebitda: '-$1.11M', ebitda_pct: 'n/m',
      customers: '3–5 pilots', headcount: '8',
      note: 'Seed raise $2.5–3.5M · Free pilots · SOC 2 readiness + HIPAA assessment begin',
    },
    {
      year: '2027', phase: 'Physician Office Expansion',
      arr_start: '$200K', arr_end: '$2.5M',
      revenue: '$1.2M', gross_profit: '$860K', gp_pct: '72%',
      personnel: '$2.3M', ai_infra: '$280K', compliance: '$320K', cyber: '$80K',
      legal: '$100K', sales_mktg: '$250K', ga: '$150K', total_opex: '$3.48M',
      ebitda: '-$2.62M', ebitda_pct: 'n/m',
      customers: '40–50 practices', headcount: '16',
      note: 'Series A raise $12–18M · SOC 2 Type II + HITRUST gap assessment · First hospital pilots Q4',
    },
    {
      year: '2028', phase: 'Hospital Rollout + Network Pitch',
      arr_start: '$2.5M', arr_end: '$8M',
      revenue: '$5.0M', gross_profit: '$3.75M', gp_pct: '75%',
      personnel: '$4.0M', ai_infra: '$750K', compliance: '$640K', cyber: '$180K',
      legal: '$150K', sales_mktg: '$800K', ga: '$250K', total_opex: '$6.77M',
      ebitda: '-$3.02M', ebitda_pct: 'n/m',
      customers: '120 practices + 10 hospitals', headcount: '28',
      note: 'Series A runway · HITRUST CSF + ONC certification · First network contracts signed',
    },
    {
      year: '2029', phase: 'Scale — Hospitals + Networks',
      arr_start: '$8M', arr_end: '$20M',
      revenue: '$13.5M', gross_profit: '$10.8M', gp_pct: '80%',
      personnel: '$7.0M', ai_infra: '$1.6M', compliance: '$650K', cyber: '$300K',
      legal: '$200K', sales_mktg: '$2.0M', ga: '$550K', total_opex: '$12.3M',
      ebitda: '-$1.5M', ebitda_pct: '-11%',
      customers: '250 practices + 30 hospitals + 2 networks', headcount: '47',
      note: 'Series B raise $35–50M · Near break-even · Network economics compounding',
    },
    {
      year: '2030', phase: 'Profitability + Acceleration',
      arr_start: '$20M', arr_end: '$40M',
      revenue: '$29M', gross_profit: '$23.8M', gp_pct: '82%',
      personnel: '$11.0M', ai_infra: '$3.0M', compliance: '$700K', cyber: '$500K',
      legal: '$300K', sales_mktg: '$4.0M', ga: '$900K', total_opex: '$20.4M',
      ebitda: '+$3.4M', ebitda_pct: '12%',
      customers: '500+ practices + 65+ hospitals + 5+ networks', headcount: '74',
      note: 'Series B runway · First operating profit · IPO/strategic options visible',
    },
  ];

  const pricing = [
    { tier: 'Solo / Small Practice', size: '1–5 providers', price: '$349/provider/mo', annual: '$4,188/provider/yr', vs: 'eClinicalWorks $449–599 · athena $140 + 6% rev share', saving: '22–40% cheaper' },
    { tier: 'Group Practice', size: '6–25 providers', price: '$249/provider/mo', annual: '$2,988/provider/yr', vs: 'Epic Garden Plot $300–500/user/mo · ModMed $500+', saving: '25–50% cheaper' },
    { tier: 'Physician Enterprise', size: '26–100 providers', price: '$199/provider/mo', annual: '$2,388/provider/yr', vs: 'Epic $200–500/user/mo', saving: '25–60% cheaper' },
    { tier: 'Rural / Community Hospital', size: '50–300 beds', price: '$149/bed/mo', annual: '$1,788/bed/yr', vs: 'MEDITECH $300K–1.2M/yr · Epic $2–5M impl + $500K+/yr maint.', saving: '30–70% cheaper' },
    { tier: 'Network Interop Layer', size: '500+ providers', price: '$25/provider/mo + platform', annual: '$300/provider/yr + $75K platform', vs: 'Epic Care Everywhere / Cogito — $1–5M/yr', saving: '60–80% cheaper' },
  ];

  const team = [
    { phase: 'H2 2026', total: 8, composition: '3 Radial Ventures (infrastructure, $190K avg) · 2 senior contractors: FHIR engineer ($200K), AI/ML engineer ($195K) · 1 clinical informaticist ($145K) · 2 Buffalo grads: full-stack + QA ($88K avg)', equity: '0.5–1.0% Buffalo hires · 0.1–0.25% contractors' },
    { phase: '2027', total: 16, composition: '+3 Buffalo grads ($90K avg) · +1 compliance/HIPAA officer ($135K) · +1 sales AE healthcare ($130K base) · +1 customer success ($105K) · +1 senior FHIR engineer ($185K) · +1 UX/product ($140K)', equity: '0.1–0.5% each' },
    { phase: '2028', total: 28, composition: '+4 Buffalo grads ($92K avg) · +1 CISO ($220K) · +2 hospital integration engineers ($190K avg) · +1 sales director ($175K) · +2 sales AEs ($132K avg) · +2 customer success ($108K avg)', equity: '0.05–0.15% each' },
    { phase: '2029', total: 47, composition: '+6 Buffalo grads ($95K avg) · +3 senior engineers (specialty modules, $195K avg) · +3 product managers ($155K avg) · +5 sales/SE ($135K avg) · +4 customer success ($110K avg)', equity: '0.02–0.05% each' },
    { phase: '2030', total: 74, composition: '+8 Buffalo grads ($98K avg) · +5 sales ($140K avg) · +4 CS ($115K avg) · +3 exec hires (CMO, CFO, VP Engineering: $220K avg) · +7 engineering ($185K avg)', equity: 'Refreshers + Series B options' },
  ];

  const compliance = [
    { cert: 'HIPAA Security Risk Assessment', timing: 'H2 2026', cost: '$20K', recurrence: 'Annual $15K', required: 'Day 1 for any customer' },
    { cert: 'SOC 2 Type I', timing: 'Q1 2027', cost: '$40K', recurrence: '—', required: 'Before enterprise sales' },
    { cert: 'SOC 2 Type II', timing: 'Q4 2027', cost: '$55K audit', recurrence: 'Annual $45K', required: 'Required by hospitals' },
    { cert: 'Cyber Insurance ($5M policy)', timing: 'H2 2026', cost: '$35K/yr', recurrence: '$60–100K by 2028', required: 'Required by hospital contracts' },
    { cert: 'HITRUST CSF Certification', timing: '2028', cost: '$150–200K total', recurrence: 'Annual $50K maintenance', required: 'Large hospital + network deals' },
    { cert: 'ONC EHR Certification', timing: '2028', cost: '$80–150K', recurrence: 'Per update cycle', required: 'CMS incentive eligibility' },
    { cert: 'Penetration Testing (annual)', timing: '2027+', cost: '$30–50K/yr', recurrence: 'Annual', required: 'SOC 2 + hospital contracts' },
    { cert: 'State Privacy Compliance (CA, TX, NY)', timing: '2028+', cost: '$40–80K legal', recurrence: 'Ongoing $20K/yr', required: 'Multi-state hospital customers' },
  ];

  return (
    <div className="space-y-12">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Financials & P&L</h2>
        <p className="text-gray-500 text-sm">5-year model · 2026–2030 · Full cost stack including AI/infrastructure, compliance certifications, cybersecurity, and healthcare-market personnel rates. Comparable: Veeva Systems, Health Catalyst early-stage trajectory.</p>
      </div>

      {/* Key milestones */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Break-even', value: '2030', sub: 'First operating profit — realistic for healthcare SaaS' },
          { label: '2030 ARR', value: '$40M', sub: 'End-of-period run rate' },
          { label: 'Gross margin (mature)', value: '80–82%', sub: 'AI + infra cost as % of revenue drops at scale' },
          { label: 'Total capital needed', value: '~$70M', sub: 'Seed + Series A + Series B through profitability' },
        ].map(({ label, value, sub }) => (
          <div key={label} className="bg-blue-50 border border-blue-100 rounded-xl p-5 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{value}</div>
            <div className="text-sm font-medium text-gray-700">{label}</div>
            <div className="text-xs text-gray-400 mt-1">{sub}</div>
          </div>
        ))}
      </div>

      {/* Cost stack callout */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <div className="font-semibold text-amber-900 mb-2">⚠️ What the prior model was missing</div>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-amber-800">
          <div><strong>AI & Infrastructure:</strong> Claude API at scale ($0.015–0.075/1K tokens), hospital-grade multi-region redundancy, on-prem Mistral GPU ($50K hardware + $20K/yr ops), audit log storage (7-yr HIPAA retention), FHIR data at scale. Grows from $75K → $3M as customer base scales.</div>
          <div><strong>Compliance Certifications:</strong> SOC 2 Type I+II, HITRUST CSF, ONC EHR certification, annual HIPAA risk assessments, state privacy law compliance (CA, TX, NY). $125K in Year 1 → $640K in Year 3. Non-negotiable for hospital customers.</div>
          <div><strong>Personnel at market rate:</strong> Senior FHIR engineers ($185–200K), clinical informaticists ($145K), CISO ($220K), healthcare sales ($130–175K base). Radial Ventures core at $190K avg. Buffalo grads at $88–98K (below SF market, meaningful equity offset).</div>
        </div>
      </div>

      {/* Pricing model */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Pricing Model</h3>
        <p className="text-sm text-gray-500 mb-4">Undercutting Epic by 25–70% across every tier. Competing with athenahealth and eClinicalWorks at the practice level. All tiers include ambient charting, decision filter, billing automation, and patient portal.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-900 text-white">
                <th className="text-left p-3 font-medium">Tier</th>
                <th className="text-left p-3 font-medium">Size</th>
                <th className="text-left p-3 font-medium text-blue-300">Our Price</th>
                <th className="text-left p-3 font-medium">Competitors</th>
                <th className="text-left p-3 font-medium text-green-300">Saving</th>
              </tr>
            </thead>
            <tbody>
              {pricing.map(({ tier, size, price, annual, vs, saving }) => (
                <tr key={tier} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-3 font-semibold text-gray-900">{tier}</td>
                  <td className="p-3 text-gray-500">{size}</td>
                  <td className="p-3 text-blue-700 font-medium">
                    <div>{price}</div>
                    <div className="text-xs text-gray-400">{annual}</div>
                  </td>
                  <td className="p-3 text-gray-500 text-xs leading-relaxed">{vs}</td>
                  <td className="p-3 font-bold text-green-600">{saving}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* P&L */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">P&L — 2026 to 2030</h3>
        <p className="text-sm text-gray-500 mb-4">Full cost stack. Personnel includes benefits overhead (15%). AI/Infra shown as combined line. Compliance and cybersecurity are explicit line items, not buried in G&A.</p>
        <div className="space-y-4">
          {pnl.map((row) => (
            <div key={row.year} className={`rounded-2xl border p-6 ${row.ebitda.startsWith('+') ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-start justify-between mb-5 flex-wrap gap-2">
                <div>
                  <div className="font-bold text-gray-900 text-lg">{row.year}</div>
                  <div className="text-sm text-blue-600 font-medium">{row.phase}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">{row.customers}</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">{row.headcount} people</span>
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${row.ebitda.startsWith('+') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    EBITDA {row.ebitda} ({row.ebitda_pct})
                  </span>
                </div>
              </div>

              {/* Revenue strip */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-xs text-gray-400 mb-1">ARR trajectory</div>
                  <div className="text-sm font-semibold text-gray-700">{row.arr_start} → {row.arr_end}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-xs text-gray-400 mb-1">Revenue recognized</div>
                  <div className="text-sm font-semibold text-gray-900">{row.revenue}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-xs text-gray-400 mb-1">Gross profit</div>
                  <div className="text-sm font-semibold text-green-700">{row.gross_profit} <span className="text-gray-400 font-normal text-xs">({row.gp_pct})</span></div>
                </div>
              </div>

              {/* OpEx breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {[
                  { label: 'Personnel (incl. benefits)', value: row.personnel },
                  { label: 'AI API + Infrastructure', value: row.ai_infra },
                  { label: 'Compliance + Certs', value: row.compliance },
                  { label: 'Cybersecurity', value: row.cyber },
                  { label: 'Legal', value: row.legal },
                  { label: 'Sales & Marketing', value: row.sales_mktg },
                  { label: 'G&A + Tools', value: row.ga },
                  { label: 'Total OpEx', value: row.total_opex, bold: true },
                ].map(({ label, value, bold }) => (
                  <div key={label} className={`rounded-lg p-2 border text-xs ${bold ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-100 text-gray-600'}`}>
                    <div className={bold ? 'text-gray-400 mb-0.5' : 'text-gray-400 mb-0.5'}>{label}</div>
                    <div className={`font-semibold ${bold ? 'text-white' : 'text-gray-800'}`}>{value}</div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                📌 {row.note}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ARR chart */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">ARR Progression</h3>
        <div className="bg-gray-950 rounded-2xl p-6 text-white">
          <div className="space-y-3">
            {[
              { year: 'End 2026', arr: 200, max: 40000, label: '$200K' },
              { year: 'End 2027', arr: 2500, max: 40000, label: '$2.5M' },
              { year: 'End 2028', arr: 8000, max: 40000, label: '$8M' },
              { year: 'End 2029', arr: 20000, max: 40000, label: '$20M' },
              { year: 'End 2030', arr: 40000, max: 40000, label: '$40M' },
            ].map(({ year, arr, max, label }) => (
              <div key={year} className="flex items-center gap-4">
                <div className="text-sm text-gray-400 w-20 flex-shrink-0">{year}</div>
                <div className="flex-1 bg-white/10 rounded-full h-5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                    style={{ width: `${Math.max((arr / max) * 100, 1.5)}%` }} />
                </div>
                <div className="text-sm font-bold text-blue-400 w-16 text-right flex-shrink-0">{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 text-xs text-gray-400">
            200× ARR growth over 4 years · Break-even in 2030 is consistent with Veeva (profitable Year 5), Health Catalyst (Year 6), and Privia Health (Year 7) trajectories in regulated healthcare SaaS
          </div>
        </div>
      </div>

      {/* Compliance detail */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Compliance Certification Roadmap</h3>
        <p className="text-sm text-gray-500 mb-4">Non-negotiable for hospital and network customers. Baked into the P&L above — not an afterthought.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border border-gray-200 font-medium text-gray-600">Certification</th>
                <th className="text-left p-3 border border-gray-200 font-medium text-gray-600">Timeline</th>
                <th className="text-left p-3 border border-gray-200 font-medium text-gray-600">Cost</th>
                <th className="text-left p-3 border border-gray-200 font-medium text-gray-600">Recurrence</th>
                <th className="text-left p-3 border border-gray-200 font-medium text-gray-600">Why Required</th>
              </tr>
            </thead>
            <tbody>
              {compliance.map(({ cert, timing, cost, recurrence, required }) => (
                <tr key={cert} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-3 border border-gray-200 font-medium text-gray-900 text-xs">{cert}</td>
                  <td className="p-3 border border-gray-200 text-blue-600 text-xs font-medium">{timing}</td>
                  <td className="p-3 border border-gray-200 text-gray-700 text-xs font-semibold">{cost}</td>
                  <td className="p-3 border border-gray-200 text-gray-500 text-xs">{recurrence}</td>
                  <td className="p-3 border border-gray-200 text-gray-500 text-xs">{required}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Team */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Team Hiring Model</h3>
        <p className="text-sm text-gray-500 mb-4">Radial Ventures infrastructure core + senior contractors for critical healthcare specialties + Buffalo community pipeline with equity (EPIC ownership model). Personnel includes 15% benefits overhead throughout.</p>
        <div className="space-y-3">
          {team.map(({ phase, total, composition, equity }) => (
            <div key={phase} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-gray-900">{phase}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{total} people</span>
                  <span className="text-xs text-gray-400">{equity}</span>
                </div>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{composition}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
          <strong>Buffalo pipeline advantage:</strong> UB CS/biomedical informatics + Roswell Park + Kaleida Health ecosystem provides a direct, loyal talent pipeline. $88–98K salaries (below SF market) offset by 0.1–1% equity stakes — following the model that made Epic's Verona, WI campus an 8,000-person engineering moat.
        </div>
      </div>

      {/* Funding */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Funding Roadmap</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { round: 'Seed', timing: 'H2 2026', amount: '$2.5–3.5M', use: 'Platform build, 3–5 pilot practices, 8-person team, SOC 2 readiness + HIPAA assessment, 18-month runway', milestone: '5 paying practices, $200K ARR, SOC 2 Type I in progress' },
            { round: 'Series A', timing: 'Q1 2027', amount: '$12–18M', use: 'Sales team, hospital pilots, compliance certifications (SOC 2 Type II, HITRUST), 16→28 person team scale, 24-month runway through 2028 hospital ramp', milestone: '$2.5M ARR, 40 practices, first 2–3 hospital contracts signed' },
            { round: 'Series B', timing: 'Q1 2029', amount: '$35–50M', use: 'Hospital acceleration, network deals, ONC certification, CISO + exec hires, 47→74 person team, runway through 2030 profitability', milestone: '$8M ARR, 10+ hospitals live, 2 network contracts, break-even visible' },
          ].map(({ round, timing, amount, use, milestone }) => (
            <div key={round} className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="font-bold text-gray-900 text-xl mb-1">{round}</div>
              <div className="text-sm text-blue-600 mb-3">{timing} · <strong>{amount}</strong></div>
              <div className="text-xs text-gray-500 mb-4 leading-relaxed">{use}</div>
              <div className="text-xs bg-green-50 text-green-700 border border-green-100 rounded-lg px-3 py-2 leading-relaxed">
                <strong>Gate:</strong> {milestone}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
          <strong>Total capital to profitability: ~$68–71M</strong> · Consistent with comparable healthcare SaaS companies (Veeva raised $73M pre-IPO; Health Catalyst raised $135M before profitability; we target a leaner path given lower implementation complexity).
        </div>
      </div>
    </div>
  );
}

const sectionComponents: Record<SectionId, React.ComponentType> = {
  opportunity: Opportunity,
  platform: Platform,
  pillars: Pillars,
  gtm: GTM,
  financials: Financials,
  technology: Technology,
  contact: Contact,
};

export default function PitchDeck() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState<SectionId>('opportunity');
  const [mobileOpen, setMobileOpen] = useState(false);

  const ActiveSection = sectionComponents[active];

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-gray-400 hover:text-gray-600">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs">M</span>
            </div>
            <span className="font-bold text-gray-900">myOpenEHR</span>
          </div>
          <span className="hidden md:inline text-gray-300 mx-2">·</span>
          <span className="hidden md:inline text-sm text-gray-400">Investor & Partner Brief</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="hidden md:inline-flex text-sm text-gray-500 hover:text-gray-900 transition-colors">
            View demo →
          </button>
          {user?.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-gray-200" />}
          <button onClick={handleLogout} className="flex items-center gap-1 text-gray-400 hover:text-gray-700 text-sm transition-colors">
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className={`${mobileOpen ? 'block' : 'hidden'} md:block w-64 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto`}>
          <nav className="p-4 space-y-1">
            {sections.map(({ id, label, icon }) => (
              <button
                key={id}
                onClick={() => { setActive(id); setMobileOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all text-left ${
                  active === id
                    ? 'bg-blue-50 text-blue-700 border border-blue-100'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="text-base">{icon}</span>
                {label}
              </button>
            ))}
            <div className="pt-4 border-t border-gray-100 mt-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors text-left border border-blue-200"
              >
                <span>🖥️</span> Live Demo
              </button>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-10">
            <ActiveSection />
          </div>
        </main>
      </div>
    </div>
  );
}
